import {
  AdornedInput,
  Adornment,
  Flex,
  FormField,
  FormHelp,
  Heading,
  IconCalendarOutline,
  IconPlusOutline,
  Input,
  Label,
  Select,
  Textarea,
  TextButton,
} from '@zillow/constellation';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import { AddressAutocomplete, type AddressResponse } from '../../../AddressAutocomplete';
import { useAddressAutocomplete } from '../../../../hooks/useAddressAutocomplete';
import { ResidenceType } from '../../../../graphql/queries';
import { validateDate, validateEmail } from '../../../../utils/validation';
import { TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { date50YearsAgo, isValidUSPhoneNumber, today } from '../../../../utils/formatters';
import { MaskedPhoneInput, MaskedZipCodeInput, validateZipCode } from '../../../Inputs';
import { US_STATES_ABBR_LIST } from '../../../../constants/applicationsConstants';
import { DatePickerInputMemoized } from '../../../DatePicker';
import { ComboBoxFix } from '../../../Combobox';
const emptyPastResidence = {
  type: ResidenceType.rent,
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  startDate: '',
  endDate: '',
  manager: {
    name: '',
    email: '',
    phone: '',
  },
};

export function PastResidencesStep() {
  const { fields, append, remove } = useFieldArray({ name: 'pastResidences' });

  function onDeleteRow(index: number) {
    remove(index);
  }

  function onAddRow() {
    append(emptyPastResidence);
  }

  return (
    <>
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        Past residences
      </Heading>

      <Flex display="flex" flexDirection="column" gap="sm">
        {fields.map((pastResidence, index) => (
          <PastResidenceEdit key={pastResidence.id} index={index} onDeleteRow={() => onDeleteRow(index)} />
        ))}
        <TextButton onClick={onAddRow} icon={<IconPlusOutline />} type="button">
          Add past residence
        </TextButton>
      </Flex>
    </>
  );
}

function PastResidenceEdit({ index, onDeleteRow }: { index: number; onDeleteRow: () => void }) {
  const {
    formState: { errors },
    register,
    setValue,
    trigger,
    watch,
  } = useFormContext();

  const [partialAddress, setPartialAddress, queryData] = useAddressAutocomplete({});
  const homeType = watch(`pastResidences[${index}].type`);

  const errorStreet = get(errors, `pastResidences[${index}].address.street`);
  const errorCity = get(errors, `pastResidences[${index}].address.city`);
  const errorState = get(errors, `pastResidences[${index}].address.state`);
  const errorZip = get(errors, `pastResidences[${index}].address.zip`);

  const startDateValue = watch(`pastResidences[${index}].startDate`);

  return (
    <>
      <Flex display="flex" justifyContent="space-between" alignItems="flex-start">
        <Heading level={6} as="h3" fontColor="textMedium">
          Past residence #{index + 1}
        </Heading>
        <TextButton onClick={onDeleteRow} fontType="bodySmallHeading" type="button">
          Delete
        </TextButton>
      </Flex>
      <Flex display="flex" flexDirection="column" gap="sm">
        <Controller
          name={`pastResidences[${index}].address.street`}
          rules={{
            required: 'Street is required',
          }}
          render={({ field: { onChange, value } }) => {
            const handleAddressSelect = (address: AddressResponse) => {
              onChange(address.street);
              setValue(`pastResidences[${index}].address.city`, address.city);
              setValue(`pastResidences[${index}].address.state`, address.state);
              setValue(`pastResidences[${index}].address.zip`, address.zip);
              trigger(`pastResidences[${index}].address.city`);
              trigger(`pastResidences[${index}].address.state`);
              trigger(`pastResidences[${index}].address.zip`);
            };

            return (
              <FormField
                label={<Label id={`past-residence-${index}-street-label`}>Street</Label>}
                control={
                  <AddressAutocomplete
                    value={value}
                    partialAddress={partialAddress}
                    onSetPartialAddress={setPartialAddress}
                    queryData={queryData}
                    onAddressSelect={handleAddressSelect}
                    onInputChange={onChange}
                    error={!!errorStreet}
                    aria-labelledby={`past-residence-${index}-street-label`}
                  />
                }
                description={errorStreet && <FormHelp>{errorStreet.message}</FormHelp>}
                error={!!errorStreet}
              />
            );
          }}
        />

        <FormField
          label={<Label>City</Label>}
          control={
            <Input
              maxLength={32}
              {...register(`pastResidences[${index}].address.city`, {
                required: 'City is required',
              })}
            />
          }
          description={errorCity && <FormHelp>{errorCity.message}</FormHelp>}
          error={!!errorCity}
        />

        <Controller
          name={`pastResidences[${index}].address.state`}
          rules={{ required: 'State is required' }}
          render={({ field }) => (
            <FormField
              label={<Label>State</Label>}
              control={
                <ComboBoxFix
                  {...field}
                  value={field.value || ''}
                  placeholder="Select a state"
                  options={US_STATES_ABBR_LIST}
                  size="md"
                />
              }
              description={errorState && <FormHelp>{errorState.message}</FormHelp>}
              error={!!errorState}
            />
          )}
        />
        <FormField
          label={<Label>ZIP code</Label>}
          control={
            <Controller
              name={`pastResidences[${index}].address.zip`}
              rules={{
                required: 'ZIP code is required',
                validate: validateZipCode,
              }}
              render={({ field }) => <MaskedZipCodeInput {...field} />}
            />
          }
          description={errorZip && <FormHelp>{errorZip.message}</FormHelp>}
          error={!!errorZip}
        />
        <Controller
          name={`pastResidences[${index}].startDate`}
          rules={{
            required: 'A moved in date is required',
            validate: (v) =>
              validateDate(v, {
                inThePast: true,
                maxYearsAgo: 50,
              }),
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label>Moved in</Label>}
              description={error && <FormHelp>{error?.message}</FormHelp>}
              error={invalid}
            >
              <AdornedInput
                input={<DatePickerInputMemoized {...field} minDate={date50YearsAgo()} maxDate={today()} />}
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline size="sm" />
                  </Adornment>
                }
              />
            </FormField>
          )}
        />

        <Controller
          name={`pastResidences[${index}].endDate`}
          rules={{
            required: 'A moved out date is required',
            validate: (v) =>
              validateDate(v, {
                inThePast: true,
                maxYearsAgo: 50,
                mustBeAfter: {
                  value: startDateValue,
                  message: 'Must be after moved in date',
                },
              }),
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label>Moved out</Label>}
              description={error && <FormHelp>{error?.message}</FormHelp>}
              error={invalid}
            >
              <AdornedInput
                input={<DatePickerInputMemoized {...field} minDate={date50YearsAgo()} maxDate={today()} />}
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline size="sm" />
                  </Adornment>
                }
              />
            </FormField>
          )}
        />
        <Controller
          name={`pastResidences[${index}].type`}
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              label={<Label>Type</Label>}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                let fieldsToClear: Array<string> = [];
                if (e.target.value !== ResidenceType.rent) {
                  fieldsToClear = [
                    ...fieldsToClear,
                    `pastResidences[${index}].manager.name`,
                    `pastResidences[${index}].manager.email`,
                    `pastResidences[${index}].manager.phone`,
                  ];
                }
                if (e.target.value !== ResidenceType.other) {
                  fieldsToClear = [...fieldsToClear, `pastResidences[${index}].description`];
                }
                fieldsToClear.forEach((fieldToClear) => setValue(fieldToClear, null));
                onChange(e.target.value);
              }}
              control={
                <Select value={value || ''} onBlur={onBlur}>
                  <option value={ResidenceType.rent}>A home I rent</option>
                  <option value={ResidenceType.own}>A home I own</option>
                  <option value={ResidenceType.other}>A different type of home</option>
                </Select>
              }
            />
          )}
        />
        {homeType === ResidenceType.rent && <LandlordContact index={index} />}
        {homeType === ResidenceType.other && (
          <FormField
            label={<Label>Description</Label>}
            control={
              <Textarea
                maxLength={TEXT_AREA_MAX_LENGTH_510}
                {...register(`pastResidences[${index}].description`)}
                resize="vertical"
              />
            }
            optional
          />
        )}
      </Flex>
    </>
  );
}

function LandlordContact({ index }: { index: number }) {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const errorLandlordName = get(errors, `pastResidences[${index}].manager.name`);
  const errorLandlordEmail = get(errors, `pastResidences[${index}].manager.email`);
  const errorLandlordPhone = get(errors, `pastResidences[${index}].manager.phone`);

  return (
    <>
      <FormField
        label={<Label>Landlord name</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`pastResidences[${index}].manager.name`, {
              required: 'A name is required',
            })}
          />
        }
        description={errorLandlordName && <FormHelp>{errorLandlordName.message}</FormHelp>}
        error={!!errorLandlordName}
      />
      <FormField
        label={<Label>Landlord contact email</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`pastResidences[${index}].manager.email`, {
              validate: validateEmail,
            })}
            type="email"
          />
        }
        description={errorLandlordEmail && <FormHelp>{errorLandlordEmail.message}</FormHelp>}
        error={!!errorLandlordEmail}
        optional
      />
      <FormField
        label={<Label>Landlord contact phone</Label>}
        control={
          <Controller
            name={`pastResidences[${index}].manager.phone`}
            rules={{
              required: 'A phone number is required',
              validate: (phone) => isValidUSPhoneNumber(phone) || 'Enter a valid phone number',
            }}
            render={({ field }) => <MaskedPhoneInput {...field} />}
          />
        }
        description={errorLandlordPhone && <FormHelp>{errorLandlordPhone.message}</FormHelp>}
        error={!!errorLandlordPhone}
      />
    </>
  );
}
